import { useState } from "react";
import { Loader2, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const EMPTY = { name: "", email: "", phone: "", address_line: "", postal_code: "", city: "" };
const FIELDS = [
  ["name", "Nom et prénom *", "col-span-2"],
  ["email", "E-mail *", "col-span-2"],
  ["phone", "Téléphone", "col-span-2"],
  ["address_line", "Adresse", "col-span-2"],
  ["postal_code", "Code postal", ""],
  ["city", "Ville", ""],
];

export default function NewCustomerForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const submit = async (e, again) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/admin/customers", form);
      toast.success(data.message);
      setForm(EMPTY);
      if (!again) setOpen(false);
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Création impossible");
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button data-testid="new-customer-button" onClick={() => setOpen(true)} className="flex items-center gap-2 rounded-full bg-amber-deep px-5 py-2.5 text-sm font-medium text-white shadow-md shadow-amber-900/15 transition-colors hover:bg-honey">
        <UserPlus size={16} /> Nouveau client
      </button>
    );
  }
  return (
    <form data-testid="new-customer-form" onSubmit={(e) => submit(e, false)} className="w-full rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-xl font-semibold text-bark">Ajouter un client</h2>
        <button type="button" data-testid="new-customer-close" onClick={() => setOpen(false)} className="text-stone-mute hover:text-bark" aria-label="Fermer"><X size={18} /></button>
      </div>
      <p className="mt-1 text-sm text-bark-soft">
        La fiche est créée sans mot de passe : le client pourra activer son compte avec « Mot de passe oublié ». Il est inscrit automatiquement à la newsletter (sauf s'il s'en était désinscrit).
      </p>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {FIELDS.map(([k, label, span]) => (
          <label key={k} className={`text-xs font-medium uppercase tracking-wider text-stone-mute ${span}`}>{label}
            <input
              data-testid={`new-customer-${k}`}
              type={k === "email" ? "email" : "text"}
              required={k === "name" || k === "email"}
              value={form[k]}
              onChange={(e) => setForm({ ...form, [k]: k === "postal_code" ? e.target.value.replace(/[^0-9A-Za-z -]/g, "") : e.target.value })}
              className="mt-1 w-full rounded-xl border border-amber-900/20 bg-cream px-3 py-2.5 text-sm normal-case tracking-normal text-bark outline-none focus:border-amber-deep"
            />
          </label>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button data-testid="new-customer-save" disabled={saving} className="flex items-center gap-2 rounded-full bg-amber-deep px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-honey disabled:opacity-60">
          {saving && <Loader2 size={15} className="animate-spin" />} Créer le client
        </button>
        <button type="button" data-testid="new-customer-save-another" disabled={saving} onClick={(e) => submit(e, true)} className="rounded-full border border-amber-800/25 px-6 py-2.5 text-sm font-medium text-bark transition-colors hover:border-amber-deep hover:text-amber-deep disabled:opacity-60">
          Créer et en ajouter un autre
        </button>
      </div>
    </form>
  );
}
